import React from "react";
import { Search, SquarePen } from "lucide-react";
import { Button } from "ui/Button";
import { Input } from "ui/Input";
import LoadingSkeleton from "ui/LoadingSkeleton";
import {
    getChatInboxUiStrings,
    resolveChatInboxLocale,
} from "i18n/chatInboxUi";

interface ChatInboxLoadingShellProps {
    /**
     * When true, render only the single-column list shell without the
     * desktop two-pane layout. Used by callers that already manage their
     * own outer surface (e.g. a mobile drawer).
     */
    embedded?: boolean;
    /** How many skeleton conversation rows to render in the list area. */
    skeletonRowCount?: number;
}

/**
 * Renders the static Instagram-style chat inbox chrome (search input, compose
 * button, Messages heading, Requests link) with skeleton rows in the
 * conversation list area.
 *
 * Shown while we are waiting for the auth context to hydrate (via
 * `RequireAuth` `loadingFallback`) and while the conversations query is
 * still fetching inside `ChatInbox`. Using the same shell in both cases
 * means the user never sees a generic single-row skeleton; the inbox
 * layout is present immediately and only the list area swaps from
 * skeletons to real rows.
 *
 * Inputs and the compose button are rendered in a disabled state so the
 * user cannot tap something that has no handler yet.
 */
const ChatInboxLoadingShell: React.FC<ChatInboxLoadingShellProps> = ({
    embedded = false,
    skeletonRowCount = 6,
}) => {
    const strings = getChatInboxUiStrings(resolveChatInboxLocale());

    const listShell = (
        <div
            className="flex h-full flex-col"
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label={strings.messagesHeading}
        >
            <div className="space-y-3 border-b border-eoe-border-main p-4 dark:border-eoe-dark-border-main">
                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search
                            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-eoe-text-sub dark:text-eoe-dark-text-sub"
                            aria-hidden="true"
                        />
                        <Input
                            value=""
                            readOnly
                            placeholder={strings.searchPlaceholder}
                            aria-label={strings.searchPlaceholder}
                            className="pl-10"
                        />
                    </div>
                    <Button
                        type="button"
                        disabled
                        size="lg"
                        className="h-10 w-10 shrink-0 bg-transparent p-0 hover:bg-transparent disabled:opacity-100 dark:bg-transparent dark:hover:bg-transparent"
                        aria-label={strings.composeAriaLabel}
                    >
                        <SquarePen className="size-6 text-eoe-text dark:text-eoe-dark-text" />
                    </Button>
                </div>

                <div className="flex items-center justify-between">
                    <div className="text-[15px] font-semibold text-eoe-text dark:text-eoe-dark-text">
                        {strings.messagesHeading}
                    </div>
                    <div className="text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {strings.requestsLink}
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-hidden">
                <div className="space-y-1 p-2">
                    <LoadingSkeleton
                        variant="entity-avatar"
                        count={skeletonRowCount}
                        avatarSize="h-12 w-12"
                        padding="p-3"
                        ariaLabel={strings.messagesHeading}
                    />
                </div>
            </div>
        </div>
    );

    if (embedded) {
        return listShell;
    }

    return (
        <div className="flex h-[calc(100vh-3.5rem)]">
            <div className="w-full border-r border-eoe-border-main dark:border-eoe-dark-border-main md:w-[360px]">
                {listShell}
            </div>
            <div
                className="hidden min-w-0 flex-1 md:block"
                aria-hidden="true"
            />
        </div>
    );
};

export default ChatInboxLoadingShell;
